import React, { useEffect, useRef } from 'react';
import { Image, Text, View } from 'react-native-web';
import setDefaultProps from 'react-native-simple-default-props';
import moment from 'moment';
import { Toaster } from 'react-hot-toast';
import * as Animatable from 'react-native-animatable';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Child Components
import AppTabBar from './AppTabBar';

// Configs
import GLOBAL from './configs/config_global';
import { setGlobalState, STATE_KEYS, useGlobalState } from './configs/config_state';
import ANIMATIONS from './configs/config_animation';

// Navigation
import * as RootNavigation from './navigation/RootNavigation';
import { navigationRef } from './navigation/RootNavigation';
import RouterStack from './navigation/Router';
import routeKeys from './navigation/RouteKeys';

export default function App() {
    // region Config Animations
    Animatable.initializeRegistryWithDefinitions({
        fadeZoom: ANIMATIONS.fadeZoom,
        fadeZoomOut: ANIMATIONS.fadeZoomOut
    });
    // endregion

    // region Navigation Ref
    useEffect(() => {
        const unsubscribe = navigationRef.addListener('state', (e) => {
            const screenKey = navigationRef.current?.getCurrentRoute()?.key;
            console.log('screen key changed', screenKey);
            setGlobalState(STATE_KEYS.CURRENT_SCREEN_KEY, screenKey);
        });
        return unsubscribe;
    }, []);
    // endregion

    // region Refs
    const mainView = useRef(null);
    // endregion

    // region Global State
    const [isDev] = useGlobalState(STATE_KEYS.IS_DEV);
    const [loadingConfig] = useGlobalState(STATE_KEYS.LOADING_CONFIG);
    const [loggedIn] = useGlobalState(STATE_KEYS.LOGGED_IN);
    const [showTabBar] = useGlobalState(STATE_KEYS.SHOW_TAB_BAR);
    const [alertShowing] = useGlobalState(STATE_KEYS.ALERT_SHOWING);
    // endregion

    // region Component Mounting
    const mounted = useRef(null);
    if (!mounted.current) {
        setDefaultProps(Text, { style: { fontFamily: 'Inter', fontSize: 18, color: GLOBAL.COLOR.BASIC.BLACK_TWO, userSelect: 'none' } });
        setDefaultProps(View, { style: { backgroundColor: 'transparent' } });
    }

    useEffect(() => {
        mounted.current = true;
        const routeConfigs = RootNavigation.currentRoute() || {};

        // Save deep link configs for later processing (in Profile screen)
        if (routeConfigs.params) {
            setGlobalState(STATE_KEYS.DEEP_LINK_DATA, { path: routeConfigs.path, params: routeConfigs.params });
        }

        // Always redirect to main stack
        if (!GLOBAL.BLANK_LOADED && !routeConfigs.key?.toLowerCase()?.includes('loading')) {
            GLOBAL.BLANK_LOADED = true;
            RootNavigation.reset(routeKeys.BLANK);
        }
    }, []);
    // endregion

    // region Render Loading View
    const renderLoadingView = () => (
        <Animatable.View
            pointerEvents={loadingConfig ? 'auto' : 'none'}
            animation={loadingConfig ? 'fadeIn' : 'fadeOut'}
            duration={600}
            style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)',
                alignItems: 'center', justifyContent: 'center'
            }}>
            {/* Logo */}
            <Image
                source={require('./images/logo_vendor_portrait.svg').default}
                style={{ width: 180, height: (71 / 111) * 180, marginTop: 64, opacity: 0.5 }}
            />

            {/* Loading Text */}
            <Text style={{ color: 'white', fontSize: 20, fontWeight: 'bold', marginTop: 24 }}>
                {loadingConfig?.loadingText || 'Loading...'}
            </Text>
        </Animatable.View>
    );
    // endregion

    // region Render Alert Backdrop
    const renderAlertBackdrop = () => (
        <Animatable.View
            pointerEvents={alertShowing ? 'auto' : 'none'}
            animation={alertShowing ? 'fadeIn' : 'fadeOut'}
            duration={400}
            style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)',
                alignItems: 'center', justifyContent: 'center'
            }}
        />
    );
    // endregion

    // region MAIN RENDER
    useEffect(() => {
        setGlobalState(STATE_KEYS.MAIN_VIEW, mainView.current);
    }, [mainView.current]);

    return(
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <View style={{ position: 'absolute', top: 0, right: 0, left: 0, bottom: 0 }}>
                {/* Main Content */}
                <View style={{ flex: 1, width: '100%', flexDirection: 'row', backgroundColor: '#EDEDED' }}>
                    {/* Left Tab Bar */}
                    {(loggedIn && showTabBar) && <AppTabBar />}

                    {/* Router */}
                    <Animatable.View ref={mainView} style={{ flex: 1, marginTop: -64 }}>
                        <RouterStack />
                    </Animatable.View>
                </View>

                {/* Footer Text */}
                {
                    !GLOBAL.DATA.USER &&
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                        {/* Copyright */}
                        <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 12, textAlign: 'center' }}>
                            {moment().year()} © Truckster{' • '}
                        </Text>

                        {/* Privacy Policy */}
                        <Text
                            onPress={() => window.open('https://gotruckster.com/privacy', '_blank')}
                            style={{ color: GLOBAL.COLOR.TEXT_GRAY, textDecorationLine: 'underline', fontSize: 12, textAlign: 'center' }}>
                            Privacy & Terms
                        </Text>
                    </View>
                }

                {/* Dev Tag */}
                {
                    isDev &&
                    <View
                        pointerEvents={'none'}
                        style={{ position: 'absolute', top: 8, right: 8, backgroundColor: GLOBAL.COLOR.MAIN, opacity: 0.7, borderRadius: 4 }}>
                        <Text style={{ fontSize: 14, color: 'white', marginHorizontal: 10, marginVertical: 4, fontWeight: 'bold' }}>
                            DEV
                        </Text>
                    </View>
                }

                {/* Toast */}
                <Toaster position={'top-center'} toastOptions={{ duration: 2000, style: { backgroundColor: 'black', color: 'white' } }} />

                {/* Loading View */}
                {renderLoadingView()}

                {/* Alert Backdrop */}
                {renderAlertBackdrop()}
            </View>
        </GoogleOAuthProvider>
    );
    // endregion
}
